import AdvancedSelect from "@edaz/edazkit/controls/AdvancedSelect";
import { Input } from "@frui.ts/bootstrap";
import DatePickerBox from "controls/datePickerBox";
import AccountNumberOrIbanAndBankCodeInput from "controls/finance/accountNumberOrIbanAndBankCodeInput";
import LicensePlateInput from "controls/licensePlateInput";
import NumberRangeInput from "controls/numberRangeInput";
import { observer } from "mobx-react-lite";
import React from "react";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { translateGeneral } from "utils/localizations";
import { onNumberInputKeyPress } from "utils/numberInput";
import type TransferRefundsViewModel from "viewModels/finance/refundApprovals/transferRefunds/transferRefundsViewModel";
import CountrySelect from "views/common/countrySelect";
import FilterView from "views/common/filter";

export interface TransferRefundsFiltersProps {
  vm: TransferRefundsViewModel;
}

const TransferRefundsFilters = observer((props: TransferRefundsFiltersProps) => {
  const { vm } = props;
  const fullTextClassNames = vm.filter.fullTextSearchEnabled ? "border-success border-2" : "";

  return (
    <FilterView vm={vm}>
      <div className="row">
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateModelAttribute("licensePlate", "countryId")}</Form.Label>
          <CountrySelect
            target={vm.filter}
            property="countryId"
            vm={vm.countrySelectViewModel}
            allowEmpty={true}
            placeholder={translateGeneral("select")}
          />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateModelAttribute("licensePlate", "licensePlate")}</Form.Label>
          <LicensePlateInput
            target={vm.filter}
            property="licensePlate"
            placeholder={translateGeneral("search")}
            className={fullTextClassNames}
          />
        </Form.Group>
        <Form.Group as={Col} md={12} lg={8} xl={6}>
          <Form.Label>{vm.translateModelAttribute("transferOriginalPayment", "senderBankAccount")}</Form.Label>
          <AccountNumberOrIbanAndBankCodeInput
            target={vm.filter}
            property="senderBankAccount"
            mode="property"
            placeholder={translateGeneral("search")}
            className={fullTextClassNames}
          />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateModelAttribute("refund", "officialRecordId")}</Form.Label>
          <Input
            target={vm.filter}
            property="officialRecordId"
            placeholder={translateGeneral("search")}
            className={fullTextClassNames}
          />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateModelAttribute("transferOriginalPayment", "variableSymbol")}</Form.Label>
          <Input
            target={vm.filter}
            property="variableSymbol"
            placeholder={translateGeneral("search")}
            type="number"
            onKeyDown={onNumberInputKeyPress}
          />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateModelAttribute("transferOriginalPayment", "paidAmount")}</Form.Label>
          <Input target={vm.filter} property="paidAmount" step={0.01} placeholder={translateGeneral("search")} type="number" />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateModelAttribute("transferOriginalPayment", "postingDate")}</Form.Label>
          <DatePickerBox target={vm.filter} property="postingDate" placeholder={translateGeneral("search")} />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateModelAttribute("refund", "refundPaymentTemplateId")}</Form.Label>
          <AdvancedSelect
            target={vm.filter}
            property="refundPaymentTemplateId"
            items={vm.transferRefundPaymentTemplatesOptions}
            allowEmpty={true}
            placeholder={translateGeneral("select")}
          />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateModelAttribute("refund", "refundAmount")}</Form.Label>
          <NumberRangeInput target={vm.filter} property="refundAmount" min={0} step={0.1} />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateModelAttribute("refund", "approvals")}</Form.Label>
          <AdvancedSelect
            target={vm}
            property="_approvalState"
            items={vm.approvalsOptions}
            allowEmpty={true}
            placeholder={translateGeneral("select")}
          />
        </Form.Group>
      </div>
    </FilterView>
  );
});

export default TransferRefundsFilters;
